
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { studentsAPI, installmentsAPI } from '../services/api';
import StudentForm  from './StudentForm';
import PaymentModal from './PaymentModal';
import { useYear } from '../context/YearContext';
import { UploadCloud, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import {
  getStudentGrossTotal,
  getStudentPendingAmount,
  isStudentFullyPaid,
} from '../utils/studentFeeStatus';

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

const waLink = (mobile, studentName, pendingAmount, schoolName) => {
  const msg =
    `${schoolName || 'గీతం స్కూల్'} నుండి నమస్కారం 🙏\n\n` +
    `ప్రియమైన తల్లిదండ్రులకు,\n` +
    `మీ పిల్లల పేరు: *${studentName}*\n` +
    `బాకీ మొత్తం: *${fmt(pendingAmount)}*\n\n` +
    `మీ పిల్లల ఫీజు ఇంకా పెండింగ్‌లో ఉంది. దయచేసి వీలైనంత త్వరగా ఫీజును చెల్లించి సహకరించగలరు.\n\n` +
    `మీ సహకారం మా విద్యార్థుల అభివృద్ధికి ఎంతో ముఖ్యమైనది. ఏవైనా సందేహాలు ఉంటే స్కూల్ కార్యాలయాన్ని సంప్రదించండి.\n\n` +
    `ధన్యవాదాలు,\n${schoolName || 'గీతం స్కూల్'}`;
  return `https://wa.me/91${mobile}?text=${encodeURIComponent(msg)}`;
};

const speakTelugu = (text) => {
  if (!window.speechSynthesis) {
    alert('మీ బ్రౌజర్ TTS కి సపోర్ట్ చేయడం లేదు. Chrome/Edge ఉపయోగించండి.');
    return;
  }
  window.speechSynthesis.cancel();
  const doSpeak = () => {
    const utter = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const teVoice = voices.find((v) => v.lang.startsWith('te'));
    if (teVoice) utter.voice = teVoice;
    utter.lang  = 'te-IN';
    utter.rate  = 0.85;
    utter.pitch = 1;
    window.speechSynthesis.speak(utter);
  };
  if (window.speechSynthesis.getVoices().length) {
    doSpeak();
  } else {
    window.speechSynthesis.onvoiceschanged = doSpeak;
  }
};

const exportCSV = (rows) => {
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const num = (v) => parseFloat(v || 0).toFixed(2);

  const headers = [
    'Admission Number', 'Student Name', 'Father Name', 'Mother Name',
    'Mobile Number', 'Class', 'Village', 'Admission Date', 'Date of Birth',
    'Caste', 'PEN Number', 'Aadhaar Number',
    'Academic Year', 'Gender', 'Fee Type',
    'Term 1 Fee', 'Term 2 Fee', 'Term 3 Fee', 'Total Academic Fee',
    'Term 1 Transport Fee', 'Term 2 Transport Fee', 'Total Transport Fee',
    'Hostel Type', 'Term 1 Hostel Fee', 'Term 2 Hostel Fee', 'Total Hostel Fee',
    'Uniform Fee', 'Books Fee',
    'Concession', 'Total Fee', 'Paid', 'Pending', 'Old Due',
    'Barcode', 'Status',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((s) =>
      [
        esc(s.admission_no),
        esc(s.student_name),
        esc(s.father_name),
        esc(s.mother_name),
        s.mobile_number || '',
        esc(s.class),
        esc(s.village),
        s.admission_date || '',
        s.dob || '',
        esc(s.caste),
        esc(s.pen_number),
        esc(s.aadhar_id),
        esc(s.academic_year),
        esc(s.gender),
        esc(s.fee_type),
        num(s.tuition_fee_term1),
        num(s.tuition_fee_term2),
        num(s.tuition_fee_term3),
        num(s.total_fee),
        num(s.bus_fee_term1),
        num(s.bus_fee_term2),
        num(s.bus_fee),
        esc(s.hostel_types),
        num(s.hostel_fee_term1),
        num(s.hostel_fee_term2),
        num(s.hostel_fee),
        num(s.uniform_fee),
        num(s.books_fee),
        num(s.concession_amount),
        num(s.gross_total),
        num(s.paid_amount),
        getStudentPendingAmount(s).toFixed(2),
        num(s.old_due_amount),
        esc(s.barcode),
        esc(s.status),
      ].join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `students_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const BROADCAST_TEMPLATES = [
  {
    label: '🎉 Holiday Notice',
    text: `గీతం స్కూల్ నుండి నమస్కారం 🙏\n\nప్రియమైన తల్లిదండ్రులకు,\n\n*సెలవు నోటీస్*\n\nమీ పిల్లలకు తెలియజేయగలరు, రేపటి (తేదీ పేర్కొనండి) నాటికి స్కూల్ సెలవు ఉంటుంది.\n\nధన్యవాదాలు,\nగీతం స్కూల్`,
  },
  {
    label: '📝 Exam Notice',
    text: `గీతం స్కూల్ నుండి నమస్కారం 🙏\n\nప్రియమైన తల్లిదండ్రులకు,\n\n*పరీక్ష నోటీస్*\n\nమీ పిల్లల పరీక్షలు త్వరలో జరగనున్నాయి. దయచేసి మీ పిల్లలను సరిగ్గా చదివించండి మరియు సమయానికి స్కూల్‌కు పంపండి.\n\nపరీక్ష వివరాలు:\n• తేదీ: _____\n• సమయం: _____\n• సబ్జెక్ట్: _____\n\nధన్యవాదాలు,\nగీతం స్కూల్`,
  },
  {
    label: '📅 Exam Timetable',
    text: `గీతం స్కూల్ నుండి నమస్కారం 🙏\n\nప్రియమైన తల్లిదండ్రులకు,\n\n*పరీక్ష టైమ్ టేబుల్*\n\nదిగువ పేర్కొన్న పరీక్ష షెడ్యూల్ ప్రకారం మీ పిల్లలను సిద్ధంగా ఉంచండి:\n\n• _____ : _____\n• _____ : _____\n• _____ : _____\n• _____ : _____\n\nపరీక్ష సమయం: ఉదయం ___ గంటలకు\n\nధన్యవాదాలు,\nగీతం స్కూల్`,
  },
  {
    label: '📢 General Announcement',
    text: `గీతం స్కూల్ నుండి నమస్కారం 🙏\n\nప్రియమైన తల్లిదండ్రులకు,\n\n*ముఖ్యమైన సమాచారం*\n\n\n\nధన్యవాదాలు,\nగీతం స్కూల్`,
  },
  {
    label: '🏫 PTM / Parent Meeting',
    text: `గీతం స్కూల్ నుండి నమస్కారం 🙏\n\nప్రియమైన తల్లిదండ్రులకు,\n\n*తల్లిదండ్రుల సమావేశం*\n\nమీరు తప్పనిసరిగా హాజరు కావలసిందిగా తెలియజేస్తున్నాం.\n\n• తేదీ: _____\n• సమయం: _____\n• స్థలం: గీతం స్కూల్\n\nదయచేసి తప్పకుండా వచ్చి సహకరించండి.\n\nధన్యవాదాలు,\nగీతం స్కూల్`,
  },
];

const BATCH_SIZE = 50; // WhatsApp Business broadcast limit is 256; keep 50 for safety

function BroadcastModal({ students, onClose }) {
  const eligible = students.filter((s) => s.mobile_number);
  const noMobile = students.filter((s) => !s.mobile_number);

  const [message,      setMessage]      = useState('');
  const [copiedNums,   setCopiedNums]   = useState(false);
  const [copiedMsg,    setCopiedMsg]    = useState(false);
  const [batchIdx,     setBatchIdx]     = useState(0); // which batch we're on (0-based)

  // Split eligible into batches of BATCH_SIZE
  const batches = [];
  for (let i = 0; i < eligible.length; i += BATCH_SIZE) {
    batches.push(eligible.slice(i, i + BATCH_SIZE));
  }
  const currentBatch = batches[batchIdx] || [];
  const allNumbers   = eligible.map((s) => s.mobile_number).join('\n');
  const batchNumbers = currentBatch.map((s) => s.mobile_number).join('\n');

  const copyText = (text, setCopied) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const isLarge = eligible.length > BATCH_SIZE;

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box large">
        <div className="modal-header">
          <h2>📢 School Broadcast — WhatsApp</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">

          {/* Quick template buttons */}
          <div style={{marginBottom:10}}>
            <label style={{fontWeight:600,fontSize:'.85rem',display:'block',marginBottom:6}}>Quick Templates:</label>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {BROADCAST_TEMPLATES.map((t) => (
                <button key={t.label} className="btn btn-secondary"
                  style={{fontSize:'.78rem',padding:'4px 10px'}}
                  onClick={() => setMessage(t.text)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message body */}
          <div className="form-group" style={{marginBottom:12}}>
            <label style={{fontWeight:600}}>Message Body</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              placeholder="మీ మెసేజ్ ఇక్కడ రాయండి... పై templates క్లిక్ చేసి edit చేయవచ్చు."
              style={{width:'100%',fontFamily:'inherit',fontSize:'.9rem',padding:'10px',border:'2px solid #ddd',borderRadius:8,resize:'vertical'}}
            />
          </div>

          {/* Recipient summary */}
          <div style={{display:'flex',gap:12,alignItems:'center',padding:'10px 14px',background:'#f0fdf4',borderRadius:8,marginBottom:12}}>
            <span style={{fontSize:'1.3rem'}}>📱</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,color:'#166534'}}>{eligible.length} parents have mobile numbers</div>
              {noMobile.length > 0 && <div style={{fontSize:'.82rem',color:'#92400e'}}>⚠️ {noMobile.length} skipped (no mobile)</div>}
            </div>
            <button
              className="btn btn-secondary"
              style={{fontSize:'.82rem'}}
              onClick={() => copyText(message || '', setCopiedMsg)}
              disabled={!message.trim()}
            >
              {copiedMsg ? '✅ Copied!' : '📋 Copy Message'}
            </button>
          </div>

          {/* HOW TO SEND — instructions box */}
          {isLarge ? (
            /* ── LARGE SCHOOL: WhatsApp Business Broadcast approach ── */
            <div style={{background:'#fffbeb',border:'2px solid #f59e0b',borderRadius:10,padding:'14px 16px',marginBottom:4}}>
              <div style={{fontWeight:700,fontSize:'.95rem',marginBottom:10,color:'#92400e'}}>
                📋 {eligible.length} students — WhatsApp Business Broadcast (Recommended)
              </div>
              <div style={{fontSize:'.85rem',color:'#78350f',lineHeight:1.7,marginBottom:12}}>
                Browser cannot open {eligible.length} tabs at once. Use <strong>WhatsApp Business</strong> Broadcast List instead — free, works for any number:
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {batches.map((batch, i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'white',borderRadius:8,border:'1px solid #fcd34d'}}>
                    <span style={{fontWeight:700,minWidth:80,color:'#92400e'}}>Batch {i+1}</span>
                    <span style={{color:'#555',fontSize:'.85rem',flex:1}}>{batch.length} students (#{i*BATCH_SIZE+1}–#{i*BATCH_SIZE+batch.length})</span>
                    <button
                      className="btn btn-secondary"
                      style={{fontSize:'.78rem',padding:'4px 12px'}}
                      onClick={() => {
                        setBatchIdx(i);
                        copyText(batch.map((s) => s.mobile_number).join('\n'), setCopiedNums);
                      }}
                    >
                      {copiedNums && batchIdx === i ? '✅ Copied!' : '📋 Copy Numbers'}
                    </button>
                  </div>
                ))}
              </div>
              <div style={{marginTop:14,padding:'10px 12px',background:'#fff7ed',borderRadius:8,border:'1px solid #fed7aa'}}>
                <div style={{fontWeight:700,fontSize:'.85rem',marginBottom:6,color:'#9a3412'}}>📱 Steps to send in WhatsApp Business:</div>
                <ol style={{margin:0,paddingLeft:20,fontSize:'.83rem',color:'#7c2d12',lineHeight:2}}>
                  <li>Click <strong>Copy Numbers</strong> for Batch 1 above</li>
                  <li>Open <strong>WhatsApp Business</strong> app on your phone</li>
                  <li>Tap <strong>New Broadcast</strong> → Add recipients → paste numbers</li>
                  <li>Click <strong>📋 Copy Message</strong> → paste in WhatsApp → Send</li>
                  <li>Repeat for each batch</li>
                </ol>
              </div>
            </div>
          ) : (
            /* ── SMALL SCHOOL: Copy numbers + message ── */
            <div style={{background:'#f0fdf4',border:'2px solid #22c55e',borderRadius:10,padding:'14px 16px',marginBottom:4}}>
              <div style={{fontWeight:700,fontSize:'.95rem',marginBottom:8,color:'#166534'}}>
                ✅ {eligible.length} students — Copy & Broadcast
              </div>
              <div style={{display:'flex',gap:10,marginBottom:10}}>
                <button
                  className="btn btn-primary"
                  style={{flex:1}}
                  onClick={() => copyText(allNumbers, setCopiedNums)}
                >
                  {copiedNums ? '✅ Numbers Copied!' : '📋 Copy All Numbers'}
                </button>
                <button
                  className="btn btn-secondary"
                  style={{flex:1}}
                  onClick={() => copyText(message || '', setCopiedMsg)}
                  disabled={!message.trim()}
                >
                  {copiedMsg ? '✅ Message Copied!' : '📋 Copy Message'}
                </button>
              </div>
              <div style={{fontSize:'.83rem',color:'#166534',lineHeight:1.8}}>
                <strong>Steps:</strong> Copy Numbers → Open WhatsApp Business → New Broadcast → Add contacts → Copy Message → Paste → Send
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function StudentList({ schoolName, schoolLogoUrl }) {
  const { year }            = useYear();
  const [students,      setStudents]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [villages,      setVillages]      = useState([]);
  const [classes,       setClasses]       = useState([]);
  const [filters,       setFilters]       = useState({ village: '', class: '', pending: '', feeType: '' });
  const [showForm,      setShowForm]      = useState(false);
  const [editStudent,   setEditStudent]   = useState(null);
  const [showPayment,   setShowPayment]   = useState(false);
  const [payStudent,    setPayStudent]    = useState(null);
  const [paymentInitialTab, setPaymentInitialTab] = useState('add');
  const [paymentHistoryOnly, setPaymentHistoryOnly] = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [search,        setSearch]        = useState('');
  const [viewMode,      setViewMode]      = useState('active'); // 'active' | 'archived'
  const [archiveTarget, setArchiveTarget] = useState(null); // student to archive/TC
  const [printTcTarget,  setPrintTcTarget]  = useState(null); // student to print TC
  const [overdueIds,    setOverdueIds]    = useState(new Set()); // student_ids with overdue installments
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [qrTarget,      setQrTarget]      = useState(null); // { student, url } for QR modal
  const [showImport,    setShowImport]    = useState(false);

  // ── Load filter meta options once ────────────────────────────────────────────
  useEffect(() => {
    if (!year) return;
    Promise.all([studentsAPI.getVillages(), studentsAPI.getClasses(year)])
      .then(([vr, cr]) => {
        setVillages(vr.data.data);
        setClasses(cr.data.data);
      })
      .catch(console.error);
  }, [year]);

  // ── Fetch students whenever filters/viewMode/year change ──────────────────────
  const [lastRefresh, setLastRefresh] = useState(null);
  const pollRef = useRef(null);

  const fetchStudents = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = { status: viewMode === 'archived' ? 'archived' : 'active' };
      if (viewMode !== 'archived') params.year = year;
      if (filters.village) params.village = filters.village;
      if (filters.class)   params.class   = filters.class;
      const res = await studentsAPI.getAll(params);
      setStudents(res.data.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('StudentList fetch error:', err.response?.data || err.message);
      const msg = err.response?.data?.message || err.message || 'Unknown error';
      setError(`Failed to load students: ${msg}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, viewMode, year]);

  const handleEditStudent = useCallback(async (studentId) => {
    setError(null);
    try {
      const res = await studentsAPI.getById(studentId);
      setEditStudent(res.data.data || null);
      setShowForm(true);
    } catch (err) {
      console.error('StudentList edit fetch error:', err.response?.data || err.message);
      const msg = err.response?.data?.message || err.message || 'Unknown error';
      setError(`Failed to load student details: ${msg}`);
    }
  }, []);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // Auto-refresh every 60 seconds (silent — no loading spinner)
  useEffect(() => {
    pollRef.current = setInterval(() => fetchStudents(true), 60000);
    return () => clearInterval(pollRef.current);
  }, [fetchStudents]);

  // ── Detect overdue installments — single bulk query instead of N per-student calls ──
  useEffect(() => {
    if (viewMode !== 'active' || students.length === 0) return;
    installmentsAPI.getOverdueIds(year)
      .then((res) => setOverdueIds(new Set(res.data.data)))
      .catch(() => setOverdueIds(new Set()));
  }, [students, viewMode, year]);

  const handleDelete = async () => {
    try {
      await studentsAPI.delete(deleteTarget.student_id);
      setDeleteTarget(null);
      fetchStudents();
    } catch {
      alert('Failed to delete student.');
    }
  };

  const handleArchive = async (status, tcDate = null) => {
    try {
      await studentsAPI.setStatus(archiveTarget.student_id, status, tcDate);
      setArchiveTarget(null);
      fetchStudents();
    } catch {
      alert('Failed to update student status.');
    }
  };

  const openPaymentsModal = (student, initialTab = 'add', options = {}) => {
    setPayStudent(student);
    setPaymentInitialTab(initialTab);
    setPaymentHistoryOnly(Boolean(options.historyOnly));
    setShowPayment(true);
  };

  // ── Client-side pending amount filter ────────────────────────────────────────
  const applyPendingFilter = (list) => {
    const f = filters.pending;
    if (!f) return list;
    return list.filter((s) => {
      const p = getStudentPendingAmount(s);
      if (f === 'has')    return p > 0;
      if (f === 'none')   return p <= 0;
      if (f === '0-5k')   return p > 0 && p <= 5000;
      if (f === '5k-10k') return p > 5000 && p <= 10000;
      if (f === '10k-25k') return p > 10000 && p <= 25000;
      if (f === '25k+')   return p > 25000;
      return true;
    });
  };

  // ── Client-side fee type filter ───────────────────────────────────────────────
  const applyFeeTypeFilter = (list) => {
    if (!filters.feeType) return list;
    return list.filter((s) => (s.fee_type || '').toLowerCase() === filters.feeType.toLowerCase());
  };

  // ── Client-side name search ─────────────────────────────────────────────────
  const afterSearch = search.trim()
    ? students.filter((s) => {
        const q = search.trim().toLowerCase();
        return (
          s.student_name.toLowerCase().includes(q) ||
          (s.father_name    || '').toLowerCase().includes(q) ||
          (s.admission_no   || '').toLowerCase().includes(q) ||
          (s.village        || '').toLowerCase().includes(q) ||
          (s.class          || '').toLowerCase().includes(q) ||
          (s.mobile_number || '').toString().includes(search)
        );
      })
    : students;
  const displayed = applyFeeTypeFilter(applyPendingFilter(afterSearch));

  // ── Footer totals ────────────────────────────────────────────────────────────
  const totals = displayed.reduce(
    (acc, s) => ({
      total_fee:   acc.total_fee   + parseFloat(s.total_fee         || 0),
      bus_fee:     acc.bus_fee     + parseFloat(s.bus_fee           || 0),
      uniform_fee: acc.uniform_fee + parseFloat(s.uniform_fee       || 0),
      books_fee:   acc.books_fee   + parseFloat(s.books_fee         || 0),
      hostel_fee:  acc.hostel_fee  + parseFloat(s.hostel_fee        || 0),
      paid:        acc.paid        + parseFloat(s.paid_amount       || 0),
      old_due:     acc.old_due     + parseFloat(s.old_due_amount    || 0),
      concession:  acc.concession  + parseFloat(s.concession_amount || 0),
      gross_total: acc.gross_total + parseFloat(s.gross_total       || 0),
      pending:     acc.pending     + getStudentPendingAmount(s),
    }),
    { total_fee: 0, bus_fee: 0, uniform_fee: 0, books_fee: 0, hostel_fee: 0, paid: 0, old_due: 0, concession: 0, gross_total: 0, pending: 0 }
  );
  const grossTotal = totals.gross_total;

  return (
    <div className="page">
      {/* ── Header ── */}
      <div className="page-header">
        <h1 className="page-title">Students — {year}</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => exportCSV(displayed)} title="Export current view to CSV">
            📊 Export CSV
          </button>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)} title="Import students from CSV">
            📥 Import CSV
          </button>
          <button
            className="btn btn-secondary"
            title="Send holiday / exam / announcement to all parents via WhatsApp"
            onClick={() => setShowBroadcast(true)}
          >
            📢 Broadcast
          </button>

          {viewMode === 'active' && (
            <button
              className="btn btn-primary"
              onClick={() => { setEditStudent(null); setShowForm(true); }}
            >
              + Add Student
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => fetchStudents()}
            title="Refresh student list"
          >
            🔄 Refresh
          </button>
          {lastRefresh && (
            <span style={{ fontSize: '0.75rem', color: '#9ca3af', alignSelf: 'center' }}>
              Updated {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* ── Active / Archived tabs ── */}
      <div className="tabs" style={{ marginBottom: 0 }}>
        <button className={`tab ${viewMode === 'active' ? 'active' : ''}`} onClick={() => setViewMode('active')}>
          👨‍🎓 Active Students
        </button>
        <button className={`tab ${viewMode === 'archived' ? 'active' : ''}`} onClick={() => setViewMode('archived')}>
          🗃️ Archived / TC Issued
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <input
          type="text"
          className="filter-search"
          placeholder="Search student or father name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          name="village"
          value={filters.village}
          onChange={(e) => setFilters((p) => ({ ...p, village: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Villages</option>
          {villages.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select
          name="class"
          value={filters.class}
          onChange={(e) => setFilters((p) => ({ ...p, class: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Classes</option>
          {classes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <select
          name="pending"
          value={filters.pending}
          onChange={(e) => setFilters((p) => ({ ...p, pending: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Pending</option>
          <option value="has">Has Pending (&gt; 0)</option>
          <option value="none">Fully Paid (0)</option>
          <option value="0-5k">Rs.1 - Rs.5,000</option>
          <option value="5k-10k">Rs.5,001 - Rs.10,000</option>
          <option value="10k-25k">Rs.10,001 - Rs.25,000</option>
          <option value="25k+">Rs.25,000+</option>
        </select>

        <select
          name="feeType"
          value={filters.feeType}
          onChange={(e) => setFilters((p) => ({ ...p, feeType: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Fee Types</option>
          <option value="Regular">Regular</option>
          <option value="Olympiad">Olympiad</option>
          <option value="IIT">IIT</option>
        </select>

        {(filters.village || filters.class || filters.pending || filters.feeType || search) && (
          <button className="btn btn-secondary" onClick={() => { setFilters({ village: "", class: "", pending: "", feeType: "" }); setSearch(""); }}>
            Clear
          </button>
        )}
      </div>

      {/* ── Error ── */}
      {error && <div className="error-banner">{error}</div>}

      {/* ── Table ── */}
      {loading ? (
        <div className="loading">Loading students…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>CLASS</th>
                <th>ADM NO</th>
                <th>STUDENT NAME</th>
                <th>FATHER NAME</th>
                <th>MOBILE</th>
                <th>VILLAGE</th>
                <th>ADMISSION</th>
                <th>FEE TYPE</th>
                {viewMode === 'archived' && <th>YEAR</th>}
                {viewMode === 'archived' && <th>TC DATE</th>}
                <th>TUITION FEE</th>
                <th>TRANSPORT FEE</th>
                <th>UNIFORM FEE</th>
                <th>BOOKS FEE</th>
                <th>HOSTEL FEE</th>
                <th style={{background:'#1e3a5f',color:'#fbbf24'}}>GROSS TOTAL</th>
                <th>PAID</th>
                <th>OLD DUE</th>
                <th>CONCESSION</th>
                <th>TOTAL PENDING</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={viewMode === 'archived' ? 20 : 19} className="no-data-cell">
                    {students.length === 0 ? 'No students found. Add your first student!' : 'No students match your search.'}
                  </td>
                </tr>
              ) : (
                displayed.map((s) => (
                  <tr key={s.student_id} className={getStudentPendingAmount(s) > 0 ? '' : 'row-cleared'}>
                    <td className="id-cell"><span className="class-badge">{s.class}</span></td>
                    <td style={{ fontSize: '0.8rem', color: '#64748b' }}>{s.admission_no || '—'}</td>
                    <td>
                      <strong title={s.notes || undefined}>{s.student_name}</strong>
                      {overdueIds.has(s.student_id) && (
                        <span className="overdue-badge" title="Has overdue installment(s)">🔴 Overdue</span>
                      )}
                      {parseFloat(s.concession_amount) > 0 && (
                        <span className="concession-badge" title={s.concession_reason || 'Concession/Scholarship'}>
                          🎓 {fmt(s.concession_amount)}
                        </span>
                      )}
                      {viewMode === 'archived' && (
                        <span className={`status-badge ${s.status}`} style={{ display: 'block', marginTop: 4 }}>
                          {s.status === 'tc_issued' ? '📄 TC Issued' : '🗃️ Archived'}
                        </span>
                      )}
                    </td>
                    <td>{s.father_name}</td>
                    <td>
                      {s.mobile_number ? (
                        <a
                          href={waLink(s.mobile_number, s.student_name, getStudentPendingAmount(s), schoolName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wa-link"
                          title="Send WhatsApp reminder"
                        >
                          {s.mobile_number}
                        </a>
                      ) : '-'}
                    </td>
                    <td>{s.village}</td>
                    <td>{fmtDate(s.admission_date)}</td>
                    <td>
                      {s.fee_type ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: s.fee_type === 'IIT' ? '#ede9fe' : s.fee_type === 'Olympiad' ? '#fef9c3' : '#f0fdf4',
                          color:      s.fee_type === 'IIT' ? '#6d28d9' : s.fee_type === 'Olympiad' ? '#92400e' : '#166534',
                        }}>
                          {s.fee_type}
                        </span>
                      ) : '-'}
                    </td>
                    {viewMode === 'archived' && <td>{s.academic_year || '-'}</td>}
                    {viewMode === 'archived' && <td>{s.tc_date ? fmtDate(s.tc_date) : '-'}</td>}
                    <td>{fmt(s.total_fee)}</td>
                    <td>{parseFloat(s.bus_fee) > 0 ? fmt(s.bus_fee) : '-'}</td>
                    <td>{parseFloat(s.uniform_fee) > 0 ? fmt(s.uniform_fee) : '-'}</td>
                    <td>{parseFloat(s.books_fee) > 0 ? fmt(s.books_fee) : '-'}</td>
                    <td>{parseFloat(s.hostel_fee) > 0 ? fmt(s.hostel_fee) : '-'}</td>
                    <td style={{ fontWeight: 600, color: '#92400e', background: '#fffbeb' }}>
                      {fmt(getStudentGrossTotal(s))}
                    </td>
                    <td className="text-green">{fmt(s.paid_amount)}</td>
                    <td className={parseFloat(s.old_due_amount) > 0 ? 'text-orange' : ''}>
                      {fmt(s.old_due_amount)}
                    </td>
                    <td className={parseFloat(s.concession_amount) > 0 ? 'text-green' : ''}>
                      {parseFloat(s.concession_amount) > 0 ? fmt(s.concession_amount) : '-'}
                    </td>
                    <td>
                      <span className={`pending-chip ${getStudentPendingAmount(s) > 0 ? 'due' : 'cleared'}`}>
                        {isStudentFullyPaid(s) ? 'Fully Paid' : fmt(getStudentPendingAmount(s))}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {viewMode === 'active' ? (
                          <>
                            <button
                              className="btn btn-sm btn-history"
                              title="Transaction History"
                              onClick={() => openPaymentsModal(s, 'history', { historyOnly: true })}
                            >Hist</button>
                            <button
                              className="btn btn-sm btn-secondary"
                              title="Edit Student"
                              onClick={() => handleEditStudent(s.student_id)}
                            >Edit</button>
                            <button
                              className="btn btn-sm btn-warning"
                              title="Archive / Issue TC"
                              onClick={() => setArchiveTarget(s)}
                            >Archive</button>
                            <button
                              className="btn btn-sm btn-info"
                              title="View Progress Card QR"
                              onClick={() => {
                                const sc = sessionStorage.getItem('gs_school_code');
                                const url = `${window.location.origin}/progress-card/${encodeURIComponent(sc)}/${s.student_id}`;
                                setQrTarget({ student: s, url });
                              }}
                            >QR</button>
                            <button
                              className="btn btn-sm btn-danger"
                              title="Delete Student"
                              onClick={() => setDeleteTarget(s)}
                            >Delete</button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn btn-sm btn-history"
                              title="Transaction History"
                              onClick={() => openPaymentsModal(s, 'history')}
                            >Hist</button>
                            {s.status === 'tc_issued' && (
                              <button
                                className="btn btn-sm btn-info"
                                title="Print Transfer Certificate"
                                onClick={() => setPrintTcTarget(s)}
                              >TC</button>
                            )}
                            <button
                              className="btn btn-sm btn-success"
                              title="Reactivate Student"
                              onClick={async () => {
                                await studentsAPI.setStatus(s.student_id, 'active');
                                fetchStudents();
                              }}
                            >Reactivate</button>
                            <button
                              className="btn btn-sm btn-danger"
                              title="Permanently Delete"
                              onClick={() => setDeleteTarget(s)}
                            >Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {displayed.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={viewMode === 'archived' ? 9 : 8}><strong>Total ({displayed.length} students)</strong></td>
                  <td><strong>{fmt(totals.total_fee)}</strong></td>
                  <td>{totals.bus_fee > 0 ? <strong>{fmt(totals.bus_fee)}</strong> : '—'}</td>
                  <td>{totals.uniform_fee > 0 ? <strong>{fmt(totals.uniform_fee)}</strong> : '—'}</td>
                  <td>{totals.books_fee > 0 ? <strong>{fmt(totals.books_fee)}</strong> : '—'}</td>
                  <td>{totals.hostel_fee > 0 ? <strong>{fmt(totals.hostel_fee)}</strong> : '—'}</td>
                  <td style={{fontWeight:700,color:'#92400e',background:'#fffbeb'}}><strong>{fmt(grossTotal)}</strong></td>
                  <td className="text-green"><strong>{fmt(totals.paid)}</strong></td>
                  <td className={totals.old_due > 0 ? 'text-orange' : ''}>{totals.old_due > 0 ? <strong>{fmt(totals.old_due)}</strong> : '—'}</td>
                  <td className="text-green"><strong>{totals.concession > 0 ? fmt(totals.concession) : '—'}</strong></td>
                  <td className={totals.pending > 0 ? 'text-red' : 'text-green'}>
                    <strong>{fmt(totals.pending)}</strong>
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ── Modals ── */}
      {showForm && (
        <StudentForm
          student={editStudent}
          schoolName={sessionStorage.getItem('gs_school_name') || ''}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); fetchStudents(); }}
        />
      )}

      {showBroadcast && (
        <BroadcastModal students={displayed} onClose={() => setShowBroadcast(false)} />
      )}




      {showPayment && payStudent && (
        <PaymentModal
          student={payStudent}
          schoolLogoUrl={schoolLogoUrl}
          initialTab={paymentInitialTab}
          historyOnly={paymentHistoryOnly}
          onClose={() => {
            setShowPayment(false);
            setPaymentInitialTab('add');
            setPaymentHistoryOnly(false);
          }}
          onSave={() => {
            setShowPayment(false);
            setPaymentInitialTab('add');
            setPaymentHistoryOnly(false);
            fetchStudents();
          }}
        />
      )}

      {deleteTarget && createPortal(
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p>
                Delete <strong>{deleteTarget.student_name}</strong>?<br />
                <small className="text-muted">All payment records will also be removed.</small>
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger"    onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Archive / TC Modal ── */}
      {archiveTarget && (
        <ArchiveModal
          student={archiveTarget}
          onClose={() => setArchiveTarget(null)}
          onConfirm={handleArchive}
        />
      )}

      {printTcTarget && (
        <PrintTCModal
          student={printTcTarget}
          onClose={() => setPrintTcTarget(null)}
        />
      )}

      {qrTarget && (
        <QRModal
          student={qrTarget.student}
          url={qrTarget.url}
          onClose={() => setQrTarget(null)}
        />
      )}

      {showImport && (
        <ImportCSVModal
          year={year}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchStudents(); }}
        />
      )}
    </div>
  );
}

// ── Archive/TC Modal component ─────────────────────────────────────────────────
function ArchiveModal({ student, onClose, onConfirm }) {
  const [action,  setAction]  = useState('archived');
  const [tcDate,  setTcDate]  = useState(new Date().toISOString().split('T')[0]);

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h2>🗃️ Archive / Issue TC — {student.student_name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              <option value="archived">Archive (left school, no TC)</option>
              <option value="tc_issued">Issue TC (Transfer Certificate)</option>
            </select>
          </div>
          {action === 'tc_issued' && (
            <div className="form-group">
              <label>TC Date</label>
              <input type="date" value={tcDate} onChange={(e) => setTcDate(e.target.value)} lang="en-GB" />
            </div>
          )}
          {getStudentPendingAmount(student) > 0 && (
            <div className="dues-warning">
              ⚠️ This student has <strong>{fmt(getStudentPendingAmount(student))}</strong> in pending dues. Please collect before archiving.
            </div>
          )}
          <p style={{ color: '#777', fontSize: '0.85rem' }}>
            The student will be moved to the <em>Archived</em> tab. Records are preserved and the student can be reactivated later.
          </p>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onConfirm(action, action === 'tc_issued' ? tcDate : null)}>
            {action === 'tc_issued' ? 'Issue TC & Archive' : 'Archive Student'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Print TC Modal component ───────────────────────────────────────────────────
function PrintTCModal({ student, onClose }) {
  const schoolName     = sessionStorage.getItem('gs_school_name')     || 'School';
  const schoolLocation = sessionStorage.getItem('gs_school_location') || '';
  const tcNo = `TC/${student.academic_year}/${String(student.student_id).padStart(4, '0')}`;

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box large">
        <div className="modal-header no-print">
          <h2>🖨️ Transfer Certificate — {student.student_name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div id="tc-print-area">
            <div className="tc-header">
              <h2 className="tc-school-name">{schoolName.toUpperCase()}</h2>
              {schoolLocation && <p className="tc-school-sub">{schoolLocation}</p>}
              <h3 className="tc-title">TRANSFER CERTIFICATE</h3>
              <div className="tc-meta">
                <span>TC No: {tcNo}</span>
                <span>Date: {fmtDate(student.tc_date)}</span>
              </div>
            </div>
            <table className="tc-table">
              <tbody>
                <tr><td>Student Name</td><td><strong>{student.student_name}</strong></td></tr>
                <tr><td>Father's Name</td><td>{student.father_name}</td></tr>
                <tr><td>Class</td><td>{student.class}</td></tr>
                <tr><td>Village</td><td>{student.village}</td></tr>
                <tr><td>Date of Admission</td><td>{fmtDate(student.admission_date)}</td></tr>
                <tr><td>Date of Leaving</td><td>{fmtDate(student.tc_date)}</td></tr>
                <tr><td>Academic Year</td><td>{student.academic_year}</td></tr>
              </tbody>
            </table>
            <div className="tc-body-text">
              <p>
                This is to certify that the above mentioned student was on the rolls of this school
                and has left on <strong>{fmtDate(student.tc_date)}</strong>.
              </p>
              <p>His / Her conduct and character during the stay at this school was <strong>Good</strong>.</p>
            </div>
            <div className="tc-footer">
              <div className="tc-sign-block">
                <p className="tc-sign-line">___________________</p>
                <p>Class Teacher</p>
              </div>
              <div className="tc-sign-block">
                <p className="tc-sign-line">___________________</p>
                <p>Principal / HM</p>
                <p><strong>{schoolName.toUpperCase()}</strong></p>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-actions no-print">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={() => window.print()}>🖨️ Print</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── QR Code Modal component ────────────────────────────────────────────────────
function QRModal({ student, url, onClose }) {
  const canvasRef = React.useRef(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    // Generate QR code using a simple inline QR generation via canvas
    // We use the QR code API for simplicity
    if (canvasRef.current) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = 250;
        canvas.height = 250;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 250, 250);
      };
      // Use a reliable QR code generation API
      img.src = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(url)}`;
    }
  }, [url]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `progress-card-qr-${student.student_name.replace(/\s+/g, '_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, textAlign: 'center' }}>
        <div className="modal-header">
          <h3>📊 Progress Card QR Code</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>{student.student_name}</p>
          <p style={{ margin: 0, color: '#666', fontSize: 13 }}>Class: {student.class} | {student.father_name}</p>
          <canvas ref={canvasRef} style={{ border: '2px solid #e0e0e0', borderRadius: 8, width: 250, height: 250 }} />
          <p style={{ margin: 0, color: '#888', fontSize: 12, wordBreak: 'break-all' }}>
            Scan this QR code to view the student's progress card
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={handleDownload}>📥 Download QR</button>
            <button className="btn btn-secondary" onClick={handleCopy}>
              {copied ? '✅ Copied!' : '📋 Copy Link'}
            </button>
            <button className="btn btn-success" onClick={() => window.open(url, '_blank')}>
              🔗 Open Card
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── CSV Import Modal ───────────────────────────────────────────────────────────
const CSV_COLUMN_MAP = {
  'adm no':                 'admission_no',
  'admission no':           'admission_no',
  'admission number':       'admission_no',
  'admission_no':           'admission_no',
  'student name':           'student_name',
  'student_name':           'student_name',
  'name':                   'student_name',
  'father name':            'father_name',
  'father_name':            'father_name',
  'mother name':            'mother_name',
  'mother_name':            'mother_name',
  'mobile':                 'mobile_number',
  'mobile number':          'mobile_number',
  'mobile_number':          'mobile_number',
  'phone':                  'mobile_number',
  'class':                  'class',
  'village':                'village',
  'admission date':         'admission_date',
  'admission_date':         'admission_date',
  'date of birth':          'dob',
  'dob':                    'dob',
  'caste':                  'caste',
  'pen number':             'pen_number',
  'pen_number':             'pen_number',
  'aadhaar number':         'aadhar_id',
  'aadhar number':          'aadhar_id',
  'aadhar_id':              'aadhar_id',
  'academic year':          'academic_year',
  'academic_year':          'academic_year',
  'gender':                 'gender',
  'fee type':               'fee_type',
  'fee_type':               'fee_type',
  'term 1 fee':             'tuition_fee_term1',
  'tuition_fee_term1':      'tuition_fee_term1',
  'term 2 fee':             'tuition_fee_term2',
  'tuition_fee_term2':      'tuition_fee_term2',
  'term 3 fee':             'tuition_fee_term3',
  'tuition_fee_term3':      'tuition_fee_term3',
  'total academic fee':     'total_fee', // store as tuition total when term breakdown is missing
  'term 1 transport fee':   'bus_fee_term1',
  'bus_fee_term1':          'bus_fee_term1',
  'term 2 transport fee':   'bus_fee_term2',
  'bus_fee_term2':          'bus_fee_term2',
  'total transport fee':    'bus_fee',
  'bus fee':                'bus_fee',
  'bus_fee':                'bus_fee',
  'transport fee':          'bus_fee',
  'hostel type':            'hostel_types',
  'hostel_types':           'hostel_types',
  'term 1 hostel fee':      'hostel_fee_term1',
  'hostel_fee_term1':       'hostel_fee_term1',
  'term 2 hostel fee':      'hostel_fee_term2',
  'hostel_fee_term2':       'hostel_fee_term2',
  'total hostel fee':       'hostel_fee',
  'hostel fee':             'hostel_fee',
  'hostel_fee':             'hostel_fee',
  'uniform fee':            'uniform_fee',
  'uniform_fee':            'uniform_fee',
  'books fee':              'books_fee',
  'books_fee':              'books_fee',
  'concession':             'concession_amount',
  'concession amount':      'concession_amount',
  'concession_amount':      'concession_amount',
  'total fee':              'gross_total',
  'total_fee':              'gross_total',
  'tuition fee':            'gross_total',
  'tuition_fee':            'gross_total',
  'paid':                   'paid_amount',
  'paid amount':            'paid_amount',
  'paid_amount':            'paid_amount',
  'pending':                '_csv_pending_hint', // not stored; used to detect net-of-concession convention
  'old due':                'old_due_amount',
  'old due amount':         'old_due_amount',
  'old_due_amount':         'old_due_amount',
  'barcode':                'barcode',
  'status':                 'status',
  'notes':                  'notes',
};

function parseCSVLine(line) {
  const result = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur.trim());
  return result;
}

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const rawHeaders = parseCSVLine(lines[0]);
  const fieldKeys  = rawHeaders.map(h => CSV_COLUMN_MAP[h.toLowerCase().trim()]);

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCSVLine(lines[i]);
    if (cells.every(c => !c)) continue;
    const row = {};
    fieldKeys.forEach((key, idx) => {
      if (key) row[key] = cells[idx] || '';
    });
    rows.push({ _rowNum: i + 1, ...row });
  }
  return { headers: rawHeaders, fieldKeys, rows };
}

function normalizeDate(val) {
  if (!val) return '';
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  // DD-MM-YYYY or DD/MM/YYYY
  const m = val.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
  // Try native parse as fallback
  const d = new Date(val);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return val;
}

function ImportCSVModal({ year, onClose, onImported }) {
  const [step,      setStep]      = useState('pick');   // pick | preview | result
  const [rows,      setRows]      = useState([]);
  const [headers,   setHeaders]   = useState([]);
  const [fieldKeys, setFieldKeys] = useState([]);
  const [unmapped,  setUnmapped]  = useState([]);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const fileRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: hdrs, fieldKeys: fks, rows: parsed } = parseCSV(ev.target.result);
      setHeaders(hdrs);
      setFieldKeys(fks);
      const unmap = hdrs.filter((h, i) => fks[i] === undefined);
      setUnmapped(unmap);
      // Normalize dates
      const normalized = parsed.map(r => ({
        ...r,
        admission_date: normalizeDate(r.admission_date),
        dob: normalizeDate(r.dob),
      }));
      setRows(normalized);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const payload = rows.map(({ _rowNum, _csv_pending_hint, ...r }) => {
        // Reconcile net-of-concession totals (e.g. external exports where
        // "Total Academic Fee" already has Concession deducted). Detect this
        // by checking whether Paid + Pending ≈ Total Fee in the CSV; if so,
        // add Concession back so Geetham's pending = total - paid - concession
        // formula matches the parent's actual due.
        const totalFee   = parseFloat(r.total_fee || 0) || 0;
        const paidAmt    = parseFloat(r.paid_amount || 0) || 0;
        const concession = parseFloat(r.concession_amount || 0) || 0;
        const csvPending = parseFloat(_csv_pending_hint || 0) || 0;
        if (totalFee > 0 && concession > 0 &&
            Math.abs((paidAmt + csvPending) - totalFee) < 1) {
          // CSV's Total Fee is post-concession → make it pre-concession for Geetham
          r.total_fee = (totalFee + concession).toFixed(2);
        }
        return r;
      });
      const res = await studentsAPI.importCSV({ rows: payload, academic_year: year });
      setResult(res.data);
      setStep('result');
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.message || err.message, errors: [] });
      setStep('result');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'Admission Number', 'Student Name', 'Father Name', 'Mother Name',
      'Mobile Number', 'Class', 'Village', 'Admission Date', 'Date of Birth',
      'Caste', 'PEN Number', 'Aadhaar Number',
      'Academic Year', 'Gender', 'Fee Type',
      'Term 1 Fee', 'Term 2 Fee', 'Term 3 Fee', 'Total Academic Fee',
      'Term 1 Transport Fee', 'Term 2 Transport Fee', 'Total Transport Fee',
      'Hostel Type', 'Term 1 Hostel Fee', 'Term 2 Hostel Fee', 'Total Hostel Fee',
      'Uniform Fee', 'Books Fee',
      'Concession', 'Total Fee', 'Paid', 'Pending', 'Old Due',
      'Barcode', 'Status',
    ];
    const csv = headers.join(',');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'student_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 780, width: '95vw' }}>

        {/* ── Header ── */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '.65rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, var(--indigo-600), var(--indigo-500))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
              <UploadCloud size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--slate-900)' }}>Import Students from CSV</h3>
              <p style={{ margin: 0, fontSize: '.75rem', color: 'var(--slate-500)' }}>Bulk add students using a spreadsheet</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">

          {/* ── Step 1: Pick file ── */}
          {step === 'pick' && (
            <div>
              {/* Download template banner */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--indigo-50)', border: '1px solid var(--indigo-100)', borderRadius: 10, padding: '.75rem 1rem', marginBottom: '1.25rem' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--indigo-700)' }}>Start with the official template</div>
                  <div style={{ fontSize: '.75rem', color: 'var(--indigo-500)', marginTop: '.1rem' }}>Download, fill it in, then upload below</div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', flexShrink: 0 }}>
                  <Download size={14} /> Download Template
                </button>
              </div>

              {/* Drop zone */}
              <div
                onClick={() => fileRef.current.click()}
                style={{
                  border: '2px dashed var(--indigo-200)', borderRadius: 14, padding: '2.5rem 1.5rem',
                  textAlign: 'center', cursor: 'pointer', background: 'var(--slate-50)',
                  transition: 'border-color .2s, background .2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--indigo-400)'; e.currentTarget.style.background = 'var(--indigo-50)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--indigo-200)'; e.currentTarget.style.background = 'var(--slate-50)'; }}
              >
                <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg, var(--indigo-100), var(--indigo-50))', border: '1px solid var(--indigo-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', color: 'var(--indigo-500)' }}>
                  <FileSpreadsheet size={26} />
                </div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--slate-800)', marginBottom: '.3rem' }}>Click to choose your CSV file</div>
                <div style={{ fontSize: '.78rem', color: 'var(--slate-400)' }}>Supports .csv format only</div>
              </div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />

              {/* Column hints */}
              <div style={{ marginTop: '1rem', background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: 8, padding: '.65rem .9rem', fontSize: '.75rem', color: 'var(--slate-500)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--slate-700)' }}>Required columns:</strong>{' '}
                Student Name, Father Name, Class. All other columns are optional — use the template for the full 35-column format.
              </div>
            </div>
          )}

          {/* ── Step 2: Preview ── */}
          {step === 'preview' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.85rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <CheckCircle2 size={16} color="var(--indigo-500)" />
                  <span style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--slate-800)' }}>{rows.length} row(s) ready to import</span>
                </div>
                <button className="btn btn-sm btn-secondary" onClick={() => { setStep('pick'); fileRef.current && (fileRef.current.value = ''); }} style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
                  <ArrowLeft size={13} /> Change File
                </button>
              </div>

              {unmapped.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.5rem', background: '#fffbeb', border: '1px solid #fbbf24', borderLeft: '4px solid #f59e0b', borderRadius: 8, padding: '.6rem .9rem', marginBottom: '.85rem', fontSize: '.8rem', color: '#78350f' }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1, color: '#d97706' }} />
                  <span>Unknown columns (will be ignored): <strong>{unmapped.join(', ')}</strong></span>
                </div>
              )}

              <div className="table-container" style={{ maxHeight: 340 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {fieldKeys.map((fk, i) => fk !== null && <th key={i}>{headers[i]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, ri) => (
                      <tr key={ri}>
                        <td style={{ color: 'var(--slate-400)', fontSize: 11 }}>{row._rowNum}</td>
                        {fieldKeys.map((fk, i) => fk !== null && (
                          <td key={i} style={{ whiteSpace: 'nowrap' }}>
                            {row[fk] || <span style={{ color: 'var(--slate-300)' }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Step 3: Result ── */}
          {step === 'result' && result && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '.75rem',
                background: result.inserted > 0 ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${result.inserted > 0 ? '#86efac' : '#fca5a5'}`,
                borderLeft: `4px solid ${result.inserted > 0 ? '#22c55e' : '#ef4444'}`,
                borderRadius: 10, padding: '1rem 1.1rem', marginBottom: '1rem',
              }}>
                {result.inserted > 0
                  ? <CheckCircle2 size={20} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
                  : <XCircle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                }
                <div>
                  <div style={{ fontWeight: 700, fontSize: '.95rem', color: result.inserted > 0 ? '#15803d' : '#b91c1c' }}>{result.message}</div>
                  {result.inserted > 0 && (
                    <div style={{ fontSize: '.82rem', color: '#16a34a', marginTop: '.2rem' }}>{result.inserted} student(s) added successfully.</div>
                  )}
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontWeight: 600, marginBottom: '.6rem', fontSize: '.85rem', color: 'var(--slate-700)' }}>
                    <AlertTriangle size={14} color="#dc2626" /> Rows with errors ({result.errors.length})
                  </div>
                  <div className="table-container" style={{ maxHeight: 220, border: '1px solid #fca5a5' }}>
                    <table className="data-table">
                      <thead><tr><th>Row</th><th>Field</th><th>Issue</th></tr></thead>
                      <tbody>
                        {result.errors.map((e, i) => (
                          <tr key={i}>
                            <td>{e.row}</td>
                            <td>{e.field}</td>
                            <td style={{ color: '#b91c1c' }}>{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          {step === 'preview' && (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={importing}>Cancel</button>
              <button className="btn btn-primary" onClick={handleImport} disabled={importing || rows.length === 0} style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                {importing ? 'Importing…' : <><UploadCloud size={15} /> Import {rows.length} Student(s)</>}
              </button>
            </>
          )}
          {step === 'result' && (
            <>
              {result?.errors?.length > 0 && (
                <button className="btn btn-secondary" onClick={() => setStep('pick')}>Fix &amp; Re-import</button>
              )}
              <button className="btn btn-primary" onClick={result?.inserted > 0 ? onImported : onClose}>
                {result?.inserted > 0 ? 'Done' : 'Close'}
              </button>
            </>
          )}
          {step === 'pick' && (
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

